import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { asAnyObject, getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { wrapAsArray } from "@cervval.js/common/src/tools/collection.array.tools";
import { isNumeric } from "@cervval.js/common/src/tools/number.tools";
import { anyReallyAny, keyof } from "@cervval.js/common/src/tools/object.tools";
import { CollectionTools, ObjectTools } from "@cervval.js/common/tools";
import { WebComponent } from "../components/webComponent";
export * from "../services/keyboard/keyboard.types"; // Keep export to avoid many breaking changes

/** Get HTML ref attribute */
export const REF_ATTRIBUTE = "ref";

/** ZIndex of modal classes for bootstrap */
export const BOOTSTRAP_DEFAULT_MODAL_Z_INDEX = 1055;
export const BOOTSTRAP_DEFAULT_BACKDROP_MODAL_Z_INDEX = 1050;

/**
 * when loading a string with a webpack loader, webpack will return a module with the default attribute
 */
export type StringLoadedFromModule = string | { default: string };

/**
 * @returns the string that is in the loaded module
 */
export function getStringLoadedFromModule(str: StringLoadedFromModule): string {
    if (typeof str === "string") {
        return str;
    } else if (str.default) {
        return str.default.toString();
    }
    console.error(new Error(str + " is not valid StringLoadedFromModule"));
    return "";
}

/**
 * Equivalent to JQuery $.ready(), calls fn once DOM is loaded and ready for manipulation
 * @param fn 
 */
export function onReady(fn: () => void): void {
    // see if DOM is already available
    if (document.readyState === "complete" || document.readyState === "interactive") {
        // call on next available tick
        setTimeout(fn, 0);
    } else {
        // call on DOMContentLoaded
        document.addEventListener("DOMContentLoaded", fn);
    }
}

/**
 * Search and get element in container by his ref attribute
 * @param container parentNode to get element from
 * @param refToFind ref string of the element to retrieve
 * @param refAttribute [default=ref] the attribute to search on
 * @param options
 * @param options.nearest is set to true, will search for the element which has the lowest depth (may be needed for layout components when not using shadow dom)
 * @returns the element or null if not found
 */
export function findElementByRef<T extends HTMLElement>(container: ParentNode, refToFind: string, refAttribute = "ref", options?: { nearest?: boolean }): T | null {
    if (options?.nearest) {
        const elements = querySelectorAllWithDepthSorted(container, `[${refAttribute}=${refToFind}]`);
        return elements[0]?.elt as T ?? null;
    }
    return container.querySelector<T>(`[${refAttribute}=${refToFind}]`);
}

/**
 * Search and get element in container by his ref attribute 
 * @param container parentNode to get element from
 * @param refToFind ref string of the element to retrieve
 * @param refAttribute [default=ref] the attribute to search on
 * @param options
 * @param options.nearest is set to true, will search for the element which has the lowest depth (may be needed for layout components when not using shadow dom)
 * @returns the element
 * @throws an error if element is not found
 */
export function getElementByRef<T extends HTMLElement>(container: ParentNode, refToFind: string, refAttribute = "ref", options?: { nearest?: boolean }): T {
    const elt = findElementByRef<T>(container, refToFind, refAttribute, options);
    if (elt == null) { throw new Error("Element with ref " + refToFind + " not found"); }
    return elt;
}

/**
 * Empty an element (remove all its children)
 */
export function empty(element: HTMLElement | null): void {
    if (element) {
        // this is the fastest solution in 2022 see https://jsbench.me/ptl76ncds1/1
        let lastChild = element.lastChild;
        while (lastChild) {
            element.removeChild(lastChild);
            lastChild = element.lastChild;
        }
    }
}

/**
 * Remove all Html Elements that have role=tooltip from the DOM within fromElement or within document
 * @param fromElement if not provided all tooltips of document are removed
 */
export function removeAllTooltips(fromElement?: HTMLElement): void {
    const tooltipsStillDisplayed = Array.from((fromElement ?? document).querySelectorAll("[role='tooltip']"));
    tooltipsStillDisplayed.forEach(t => t.remove());
}

/**
 * Set the title attribute of the element.
 * If string is null or undefined, the title attribute is removed so that parent title can be used instead.
 */
export function setTitle(element: HTMLElement, title: string | null | undefined): void {
    if (title == null) {
        // If title was set, remove it
        const TITLE = keyof<HTMLElement>("title");
        if (element.attributes.getNamedItem(TITLE) != null) {
            element.attributes.removeNamedItem(TITLE);
        }
    } else {
        element.title = title;
    }
}

/**
 * Available type for html content
 * - HTMLElement => DOM node to append in a HTMLElement parent
 * - string => A valid DOM string to append in a HTMLElement parent (ex: `<div class="h-100 w-100"></div>`)
 */
export type AcceptedHtmlElementContent = string | HTMLElement;

/** Define css styling with cssStyle declaration available */
export type CssStyleFields = Partial<Record<keyof CSSStyleDeclaration, string>>;

/** Options passed to define a HTMLElement behavior */
export interface HtmlElementOptions {

    /** Add classes to element (one or multiple classes) */
    cssClass?: string | string[];

    /** Define innerHtml of element */
    innerHTML?: (string | Node) | (string | Node)[];// Should accepts "Node" due to more generic element than HTMLElement (ex: like documentFragment)

    /** Define attributes of element */
    attributes?: Record<string, string>;

    /** Define css styling */
    style?: CssStyleFields;
}

/**
 * Set options of an HTMLElement 
 * - Style
 * - Css classes
 * - InnerHtml
 * - Attributes
 * @returns element in parameter
 */
export function setHtmlElementOptions(element: HTMLElement, options: HtmlElementOptions): HTMLElement {

    // Css classes definition
    if (options?.cssClass) {
        element.classList.add(...wrapAsArray(options.cssClass).filter(Boolean));// Remove empty string classes
    }

    // Attributes definition
    if (options?.attributes) {
        setHTMLElementAttributes(element, options.attributes);
    }

    // Inner html definition
    if (options?.innerHTML) {
        setHTMLElementContent(element, ...wrapAsArray(options.innerHTML));
    }

    // Css styling definition
    if (options?.style) {
        setHTMLElementStyle(element, options.style);
    }

    return element;
}

/**
 * Create a HTMLElement, with options
 * @returns a HTMLElement typed with provided options
 */
export function createHtmlElement<K extends keyof HTMLElementTagNameMap>(

    /** Define tag of element */
    tag: K,

    /** Options of element definition */
    options?: HtmlElementOptions
): HTMLElementTagNameMap[K] {
    const elt = document.createElement(tag);

    // Set options if needed
    if (options) {
        setHtmlElementOptions(elt, options);
    }

    return elt;
}


/**
 * Create a WebComponent from a tagName, with options
 * ! Provide a valid/loaded tagName !
 * @returns a WebComponent typed with provided options
 */
export function createComponent<K extends WebComponent>(

    /**
     * Component to create: tag-name of Component to create (accepts CAPS)
     */
    component: string,

    /** Options of element definition */
    options?: HtmlElementOptions
): K {
    const webComponent = document.createElement(component.toLowerCase()) as K;

    // Set options if needed
    if (options) {
        setHtmlElementOptions(webComponent, options);
    }
    return webComponent;
}


/**
 * Set content to a HTMLElement, accepts DOM string or HtmlElement value
 * @returns element in parameter
 */
export function setHTMLElementContent(element: HTMLElement, ...contents: (string | Node)[]): HTMLElement {

    // Handle no given content, don't execute useless code
    if (!contents.length) { return element; }// FIXME - AMT - Pour moi, il faudrait que cette ligne soit après "empty(...)" pour vider l'élément, à discuter

    // Empty element first, to respect "set" behavior
    empty(element);

    // Append content
    appendHTMLElementContent(element, ...contents);

    return element;
}

/**
 * Add content to a HTMLElement, accepts DOM string or HtmlElement value
 * @returns element in parameter
 */
export function appendHTMLElementContent(element: HTMLElement, ...contents: (string | Node)[]): HTMLElement {
    for (const item of contents) {
        if (item instanceof Node) {
            element.append(item);
        } else if (item != null && typeof item === "string") {
            element.insertAdjacentHTML("beforeend", getStringLoadedFromModule(item));
        }
    }
    return element;
}

/**
 * Add css styling to a HTMLElement
 * @returns element in parameter
 */
export function setHTMLElementStyle(element: HTMLElement, style: CssStyleFields): HTMLElement {
    for (const [key, value] of Object.entries(style)) {
        setProp(element.style, key, value);// Add style: key = value
    }
    return element;
}

/**
 * Set attributes to a HTMLElement
 * @returns element in parameter
 */
export function setHTMLElementAttributes(element: HTMLElement, attributes: Record<string, string>): HTMLElement {
    for (const [key, value] of Object.entries(attributes)) {
        element.setAttribute(key, value);// Add attribute: key = value
    }
    return element;
}

/**
 * Add or remove attribute element from given conditions
 * @returns element in parameter
 */
export function toggleAttribute(element: HTMLElement, attributeIsPresent: boolean | undefined, attribute: string, attributeValue: string): HTMLElement {
    if (attributeIsPresent) {
        element.setAttribute(attribute, attributeValue);
    } else {
        element.removeAttribute(attribute);
    }
    return element;
}

/**
 * Set attribute to a HTMLElement, remove the attribute if value is undefined or null
 * @returns element in parameter
 */
export function setAttribute(element: HTMLElement, key: string, value: string | undefined | null): HTMLElement {
    if (value != null) {
        element.setAttribute(key, value);
    } else {
        element.removeAttribute(key);
    }
    return element;
}

/**
 * @deprecated use createHtmlElement
 * @returns a HtmlDivElement with provided options
 */
export function createDiv(options?: { cssClass?: string, innerHTML?: string }): HTMLDivElement {
    return createHtmlElement("div", options);
}

/**
 * Create a style element containing the provided css string
 * @param css a string or a string loaded from a module (ie in a default attribute)
 */
export function createStyleElement(css: StringLoadedFromModule): HTMLStyleElement {
    const style = document.createElement("style");
    style.innerHTML = getStringLoadedFromModule(css);
    return style;
}

// this variable keeps the loaded uids so that we don't load twice the same css
const _appendCssStringLoadedUids = {};

/**
 * @returns true if style with uid cssUid was already append with appendCssString function
 */
export function wasCssStringAlreadyAppend(cssUid: string): boolean {
    return getProp(_appendCssStringLoadedUids, cssUid) != null;
}

/**
 * Registers a css string in the document head
 * @param css a string or a string loaded from a module (ie in a default attribute)
 * @param cssUid a uid that we check before loading the css, if the cssUid was already loaded, we don't load it again
 */
export function appendCssString(css: StringLoadedFromModule, cssUid?: string): void {
    if (cssUid) {
        if (getProp(_appendCssStringLoadedUids, cssUid)) {
            // already loaded
            return;
        }
        setProp(_appendCssStringLoadedUids, cssUid, 1);
    }
    const style = createStyleElement(css);
    document.head.append(style);
}

/**
 * Set attributes for all components
 * @param component Component to set option
 * @param webComponentOptions List of options - casted in string
 * @deprecated you should use WebComponentOptions instead
 */
export function setComponentsAttributes<WebComponentOptionsT extends Record<string, anyReallyAny>>(component: WebComponent, webComponentOptions: WebComponentOptionsT): void {
    for (const attributeName in webComponentOptions) {
        component.setAttribute(attributeName, String(webComponentOptions[attributeName]));
    }
}

/**
 * @see https://github.com/jquery/jquery/blob/c66d4700dcf98efccb04061d575e242d28741223/src/css/hiddenVisibleSelectors.js
 * ! Elements with visibility: hidden or opacity: 0 are considered visible, since they still consume space in the layout.
 * @returns true if element or one of its ancestors is hidden
 */
export function isHidden(element: HTMLElement): boolean {
    return !isVisible(element);
}

/**
 * @see isHidden
 * ! Elements with visibility: hidden or opacity: 0 are considered visible, since they still consume space in the layout.
 * @returns true if element and all its ancestors are visible
 */
export function isVisible(element: HTMLElement): boolean {
    return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
}

/**
 * Get all parents nodes of a given element
 *
 * @param elt to retrieve parents
 */
export function getAllParents(elt: HTMLElement): HTMLElement[] {
    const parents = [];

    let element: HTMLElement | null = elt;
    while (element) {
        parents.unshift(element);
        element = element.parentElement;
    }
    return parents;
}

/**
 * Find the first ancestor of elt that matches
 */
export function findFirstMatchingAncestor(elt: HTMLElement, match: (ancestor: HTMLElement) => boolean): HTMLElement | undefined {
    let ancestor: HTMLElement | null = elt.parentElement;
    while (ancestor) {
        if (match(ancestor)) {
            return ancestor;
        }
        ancestor = ancestor.parentElement;
    }
    return undefined;
}

/**
 * Find the first ancestor of elt that matches or directly elt if it matches
 */
export function findNearestMatch(elt: HTMLElement, match: (ancestor: HTMLElement) => boolean): HTMLElement | undefined {
    let ancestor: HTMLElement | null = elt;
    while (ancestor) {
        if (match(ancestor)) {
            return ancestor;
        }
        ancestor = ancestor.parentElement;
    }
    return undefined;
}

/**
 * Get child nodes of a given element in an array (you can use this to store nodes - you can't store childNodes because it is dynamic)
 */
export function getChildNodes(elt: HTMLElement): ChildNode[] {
    const children: ChildNode[] = [];

    elt.childNodes.forEach((node) => {
        children.push(node);
    });

    return children;
}

/**
 * Use HTMLTemplateElement.innerHTML to convert the string to HTML elements. 
 * @see https://stackoverflow.com/questions/494143/creating-a-new-dom-element-from-an-html-string-using-built-in-dom-methods-or-pro
 * @see https://jsbench.me/bcle4qry5a/1 for comparison with DOMParser
 * 
 * @param htmlString representing html code
 * @returns The first HTMLElement from the 'html string' if any or a Text node if no element is found or undefined if empty
 * 
 * Warning: HTMLElement typing is convenient but might not always be true, you may get a Text.
 * Good to know: Any HTML content created will be considered as connected due to the use of template.
 */
export function htmlStringToElement(htmlString: string): HTMLElement | undefined {
    if (htmlString == null || htmlString === "") {
        // Shortcut if string is empty
        return undefined;
    }

    // Convert string to HTML elements
    const template = document.createElement("template");
    template.innerHTML = htmlString;

    // Prefer returning the first element to avoid white space problems (https://developer.mozilla.org/fr/docs/Web/API/Document_Object_Model/Whitespace)
    // If no Element is found, return the first child which might be a Text.
    return (template.content.firstElementChild ?? template.content.firstChild ?? undefined) as HTMLElement | undefined;
}

/** Convert an html string to an array of HTML Elements */
export function htmlStringToElements(htmlString: string): HTMLElement[] {
    if (htmlString == null || htmlString === "") { return []; }
    const htmlElements: HTMLElement[] = [];
    document.createRange().createContextualFragment(htmlString).childNodes.forEach((node) => {
        htmlElements.push(node as HTMLElement);
    });
    return htmlElements;
}

/**
 * Parse given text from "html text" to "string", to keep only human readable text
 * "<span>text</span>" => "text"
 * 
 * Note, this is not equivalent to htmlStringToElement(htmlString)?.innerText ?? "";
 * htmlStringToElement("<b>str</b> other")?.innerText => "str"
 * htmlStringToElements("<b>str</b> other").map(e => e.innerText).join("") => "str"
 * htmlStringToText("<b>str</b> other")?.innerText => "str other"
 * 
 * @param htmlString representing html code
 * @param options
 * @param options.replaceLineBreak br elements in the html string will be replaced by the provided string (for example "\r\n")
 */
export function htmlStringToText(htmlString: string, options?: { replaceLineBreak: string }): string {
    if (htmlString == null || htmlString === "") { return ""; }
    const parser = new DOMParser();
    const virtualDom = parser.parseFromString(htmlString, "text/html");
    if (options?.replaceLineBreak) {
        // brs will be removed from the string, we append the replacement text after each br so they appear in the final string
        virtualDom.querySelectorAll("br").forEach((br, key, parent) => {
            br.after(options.replaceLineBreak);
        });
    }
    return virtualDom.body.innerText;
}

/**
 * Parse given text from "html text" to "string", to keep only human readable text and br are transformed to \n
 * 
 * @see htmlStringToText
 */
export function htmlStringToMultilineText(htmlString: string): string {
    return htmlStringToText(htmlString, { replaceLineBreak: "\r\n" });
}

/** @returns true if given element is a WebComponent */
export function isWebComponent(element: anyReallyAny): element is WebComponent {
    return element instanceof HTMLElement && keyof<WebComponent>("componentNumber") in element;
}

//#region querySelectorAllWithDepthSorted
type ElementAndDepth = { elt: Element, depth: number };

/**
 * compare ElementAndDepth by depth
 */
function _compareElementAndDepth(e1: ElementAndDepth, e2: ElementAndDepth): number {
    return ObjectTools.compareFields(e1, e2, "depth");
}

/**
 * returns the elements returned by querySelectorAll + computes the depth for each element
 * @returns ElementAndDepth array sorted by depth
 */
export function querySelectorAllWithDepthSorted(parentElement: ParentNode, querySelector: string): ElementAndDepth[] {
    const elements: ElementAndDepth[] = [];
    const children = parentElement.querySelectorAll(querySelector);
    children.forEach((elt) => {
        let depth = 1;
        let parent = elt.parentElement;
        while (parent && parent !== parentElement) {
            depth++;
            parent = parent.parentElement;
        }
        CollectionTools.binaryInsert(elements, { elt, depth }, true, _compareElementAndDepth);
    });
    return elements;
}

//#endregion querySelectorAllWithDepthSorted

/**
 * Download file params
 */
export interface DownloadFile {

    /** Data to export into file */
    data: anyReallyAny;

    /** File name */
    name: string;

    /** File extension (json, xlsx, ...) */
    extension: string;

    /** Mime type @see https://developer.mozilla.org/fr/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types */
    mimeType: string;
}

/**
 * Download file
 *
 * TODO: Remove function
 * @deprecated Use blob.tools instead
 * 
 * @param params
 * @param params.data Data to export into file
 * @param params.name File name
 * @param params.extension File extension
 * @param params.mimeType Mime type to instantiate Blob
 */
export function downloadFile(params: DownloadFile): void {
    // Concat name and extension to have file name
    const filename = `${params.name}.${params.extension}`;

    // Instantiate Blob
    const blob = new Blob([params.extension === "json" ? JSON.stringify(params.data) : params.data], { type: params.mimeType });

    // Create a temporary <a> link with blob URL to download file
    const a = document.createElement("a");
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.download = filename;

    // Click on temporary <a> to download file
    a.click();
    window.URL.revokeObjectURL(objectUrl);

    // Remove temporary <a>
    a.remove();
}

/**
 * Dispatch event on input inside component
 */
export function dispatchEventOnInput(input: HTMLElement | null, event: Event): boolean {
    if (input == null) { return false; }
    // if the component has a dispatchEventOnInput function it means it is a wrapper around an input
    if (typeof asAnyObject(input)["dispatchEventOnInput"] === "function") {
        return asAnyObject(input)["dispatchEventOnInput"](event);
    }
    return input.dispatchEvent(event);
}

/**
 * Ignore resize observer loop limit error
 * Ensure cb will be called in an animation frame
 * => This error means that ResizeObserver was not able to deliver all observations within a single animation frame.
 */
export function ignoreResizeObserverLoopLimit(entries: ResizeObserverEntry[], cb: () => void): void {
    if (!Array.isArray(entries) || !entries.length) {
        return;
    }

    // We wrap it in requestAnimationFrame to avoid this error - ResizeObserver loop limit exceeded
    window.requestAnimationFrame(() => {// @see https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
        cb();
    });
}

/** Retrieve px value from string width */
export function getPxValue(stringWidth: string | number, p: { parentWidth?: number, parentElement?: HTMLElement }): number | undefined {
    if (typeof stringWidth === "number") {
        return stringWidth;
    }

    const numberValue = parseFloat(stringWidth);
    if (!isNumeric(numberValue)) { return 0; }

    const parentWidth = p.parentWidth ?? p.parentElement?.clientWidth;
    if (parentWidth == null) {
        getLogConsole().warn("Impossible to retrieve pxValue from parent, no parentWidth or parentElement given");
        return undefined;
    }

    if (stringWidth.includes("px")) {
        return numberValue;
    }
    if (stringWidth.includes("%") && parentWidth) { // avoid dividing by 0
        return Math.floor(numberValue / 100 * parentWidth);
    }
    if (stringWidth.includes("em")) {// Works with "rem" and "em"
        return numberValue * parseFloat(getComputedStyle(p?.parentElement ?? document.documentElement).fontSize);
    }

    return undefined;
}

/** @returns true if element is scrollable */
export function isScrollable(el: HTMLElement): boolean {
    return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
}